import {
  cloneElement,
  type ComponentChildren,
  createElement,
  Fragment,
  type VNode,
} from "preact";
import { useRef } from "preact/hooks";
import type { MarkedToken, Token, Tokens } from "marked";
import type { Entity, Source } from "@fab-judge/schema";
import { CardRef } from "./CardRef";
import { FabText } from "./FabText";
import { RuleRef } from "./RuleRef";
import {
  createLexCache,
  incrementalTokens,
  type LexCache,
} from "./markdownLex";

/**
 * GFM answer rendering.
 *
 * `marked` does the parsing (tables, task lists, strikethrough, autolinks, …)
 * and is lenient with the partial Markdown seen mid-stream; we map its token
 * tree to Preact nodes rather than HTML, so nothing is injected via
 * `innerHTML`. Lexing is incremental (see `markdownLex.ts`) so streaming only
 * re-parses the unstable tail.
 *
 * Bracketed text becomes an inline reference: rule refs (`[1.3.2a]`) open a
 * `RuleRef`, and card names the model cited (`[Sink Below]`) open a `CardRef`.
 * `{p}`-style FAB symbols still flow through `FabText`.
 */

// Any bracketed group; each is classified as a rule ref or a card ref.
const BRACKET = /\[([^\[\]]+)\]/g;
const RULE_PART = /^[0-9]+(?:\.[0-9]+)*[a-z]?$/;

interface RenderContext {
  byRef: Map<string, Source>;
  cardByName: Map<string, Entity>;
}

export function Markdown({
  text,
  sources,
  cards,
  streaming = false,
}: {
  text: string;
  sources?: Source[];
  cards?: Entity[];
  streaming?: boolean;
}) {
  const byRef = new Map<string, Source>();
  for (const source of sources ?? []) {
    if (source.ref) byRef.set(source.ref, source);
  }
  // Only cards the server validated from the model's citations are linkable, so
  // an incidental `[word]` in the answer never renders as a card.
  const cardByName = new Map<string, Entity>();
  for (const card of cards ?? []) {
    cardByName.set(card.name.toLowerCase(), card);
  }
  const ctx: RenderContext = { byRef, cardByName };

  const cacheRef = useRef<LexCache>(createLexCache());
  // While streaming, lex incrementally and show a caret; once complete, a
  // single whole-document lex resolves anything spanning block boundaries.
  const tokens = incrementalTokens(cacheRef.current, text, !streaming);
  const blocks = renderBlocks(tokens, ctx);
  if (streaming) appendCaret(blocks);
  return <>{blocks}</>;
}

function renderBlocks(
  tokens: Token[] | undefined,
  ctx: RenderContext,
): ComponentChildren[] {
  const out: ComponentChildren[] = [];
  for (const token of (tokens ?? []) as MarkedToken[]) {
    // `space`/`def` carry no output; `checkbox` is rendered from the list item
    // (its raw text would otherwise duplicate the checkbox).
    if (
      token.type === "space" ||
      token.type === "def" ||
      token.type === "checkbox"
    ) {
      continue;
    }
    out.push(renderBlock(token, ctx));
  }
  return out;
}

function renderBlock(
  token: MarkedToken,
  ctx: RenderContext,
): ComponentChildren {
  switch (token.type) {
    case "paragraph":
      return <p class="md-p">{renderInline(token.tokens, ctx)}</p>;

    case "heading":
      return createElement(
        `h${token.depth}`,
        { class: "md-heading" },
        renderInline(token.tokens, ctx),
      );

    case "blockquote":
      return (
        <blockquote class="md-quote">
          {renderBlocks(token.tokens, ctx)}
        </blockquote>
      );

    case "list": {
      const items = token.items.map((item, index) => (
        <li key={index} class={item.task ? "md-task" : undefined}>
          {item.task && (
            <input
              class="md-checkbox"
              type="checkbox"
              checked={Boolean(item.checked)}
              disabled
            />
          )}
          {renderBlocks(item.tokens, ctx)}
        </li>
      ));
      const start =
        token.ordered && typeof token.start === "number" && token.start !== 1
          ? token.start
          : undefined;
      return createElement(
        token.ordered ? "ol" : "ul",
        { class: "md-list", start },
        items,
      );
    }

    case "code":
      return (
        <pre class="md-pre">
          <code>{token.text}</code>
        </pre>
      );

    case "hr":
      return <hr class="md-hr" />;

    case "table":
      return renderTable(token, ctx);

    case "text":
      return token.tokens && token.tokens.length > 0
        ? <>{renderInline(token.tokens, ctx)}</>
        : <>{renderText(token.text, ctx)}</>;

    case "html":
      // Raw HTML is shown literally, never injected.
      return <p class="md-p">{renderText(token.text, ctx)}</p>;

    default:
      return <>{renderText(token.raw, ctx)}</>;
  }
}

function renderTable(
  token: Tokens.Table,
  ctx: RenderContext,
): ComponentChildren {
  return (
    <div class="md-table-wrap">
      <table class="md-table">
        <thead>
          <tr>
            {token.header.map((cell, index) => (
              <th
                key={index}
                style={cell.align ? { textAlign: cell.align } : undefined}
              >
                {renderInline(cell.tokens, ctx)}
              </th>
            ))}
          </tr>
        </thead>
        {token.rows.length > 0 && (
          <tbody>
            {token.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    style={cell.align ? { textAlign: cell.align } : undefined}
                  >
                    {renderInline(cell.tokens, ctx)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
}

function renderInline(
  tokens: Token[] | undefined,
  ctx: RenderContext,
): ComponentChildren[] {
  return ((tokens ?? []) as MarkedToken[]).map((token) =>
    renderInlineToken(token, ctx)
  );
}

function renderInlineToken(
  token: MarkedToken,
  ctx: RenderContext,
): ComponentChildren {
  switch (token.type) {
    case "text":
      return token.tokens && token.tokens.length > 0
        ? <>{renderInline(token.tokens, ctx)}</>
        : <>{renderText(token.text, ctx)}</>;

    case "escape":
      return <>{renderText(token.text, ctx)}</>;

    case "strong":
      return <strong>{renderInline(token.tokens, ctx)}</strong>;

    case "em":
      return <em>{renderInline(token.tokens, ctx)}</em>;

    case "del":
      return <del>{renderInline(token.tokens, ctx)}</del>;

    case "codespan":
      return <code class="md-code">{token.text}</code>;

    case "br":
      return <br />;

    case "link": {
      const href = safeHref(token.href);
      const label = token.tokens && token.tokens.length > 0
        ? renderInline(token.tokens, ctx)
        : renderText(token.text, ctx);
      return href
        ? (
          <a class="md-link" href={href} target="_blank" rel="noreferrer">
            {label}
          </a>
        )
        : <>{label}</>;
    }

    // Don't embed remote images; show the alt text instead.
    case "image":
      return <>{renderText(token.text, ctx)}</>;

    // Raw HTML is shown literally, never injected.
    case "html":
      return <>{renderText(token.text, ctx)}</>;

    case "checkbox":
      return null;

    default:
      return <>{renderText(token.raw, ctx)}</>;
  }
}

/** Split a text run into FAB-symbol spans and inline rule/card references. */
function renderText(
  text: string,
  ctx: RenderContext,
): ComponentChildren[] {
  const out: ComponentChildren[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = BRACKET.exec(text)) !== null) {
    if (match.index > last) {
      out.push(<FabText text={text.slice(last, match.index)} />);
    }

    const group = match[1] ?? "";
    if (isRuleCitation(group)) {
      out.push(renderCitation(group, ctx.byRef));
    } else {
      const card = ctx.cardByName.get(group.trim().toLowerCase());
      out.push(
        card ? <CardRef name={card.name} /> : <FabText text={match[0]} />,
      );
    }

    last = match.index + match[0].length;
  }

  if (last < text.length) out.push(<FabText text={text.slice(last)} />);
  return out;
}

/** True when every comma-separated part looks like a rule number. */
function isRuleCitation(group: string): boolean {
  const parts = group
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length > 0 && parts.every((part) => RULE_PART.test(part));
}

/** `[1.1.2, 1.1.3]` -> bracketed, individually clickable refs. */
function renderCitation(
  group: string,
  byRef: Map<string, Source>,
): ComponentChildren {
  const refs = group
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return (
    <span class="citation">
      [
      {refs.map((ref, index) => (
        <Fragment key={ref}>
          {index > 0 && ", "}
          <RuleRef ruleRef={ref} source={byRef.get(ref)} />
        </Fragment>
      ))}
      ]
    </span>
  );
}

/** Only allow safe schemes in markdown links (drop `javascript:` etc.). */
function safeHref(href: string): string | null {
  return /^(?:https?:|mailto:)/i.test(href) ? href : null;
}

function isVNode(value: ComponentChildren): value is VNode {
  return typeof value === "object" && value !== null && "type" in value;
}

/** Append the streaming caret to the last block so it stays on the last line. */
function appendCaret(blocks: ComponentChildren[]) {
  const index = blocks.length - 1;
  const last = blocks[index];
  if (!isVNode(last)) {
    blocks.push(<span class="caret" />);
    return;
  }
  const inline = last.type === "p" ||
    (typeof last.type === "string" && last.type.startsWith("h"));
  if (inline) {
    blocks[index] = cloneElement(last, {}, [
      last.props.children,
      <span class="caret" />,
    ]);
  } else {
    blocks.push(<span class="caret" />);
  }
}
